import React, { useEffect } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { baseURL } from '../../utils/constants/env';
import { setSharedSocket } from '../../utils/socket';
import SystemNotificationDialog from '../SystemNotificationDialog/SystemNotificationDialog';

const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation();
    const [systemNotification, setSystemNotification] = React.useState<any>(null);

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        // Initialize socket connection
        // Extract base URL from API URL (remove /api/v1)
        const socketBaseUrl = baseURL.replace('/api/v1', '');

        const newSocket = io(socketBaseUrl, {
            auth: { token },
            query: { token },
            transports: ['websocket'],
            timeout: 10000,
            reconnectionAttempts: 5
        });
        setSharedSocket(newSocket);

        newSocket.on('connect', () => {
            console.log('SocketIO connected');
        });

        newSocket.on('connect_error', (error) => {
            console.log('SocketIO connection error:', error.message);
        });

        newSocket.on('new_notification', (data) => {
            console.log('Received notification via SocketIO:', data);

            // Play sound
            const audio = new Audio('/games/sounds/correct.mp3');
            audio.play().catch(e => {
                console.warn('Browser blocked autoplay sound because user has not interacted with the document yet.');
            });

            // Helper to strip HTML tags for toasts and desktop notifications
            const stripHtml = (html?: string | null) => {
                if (!html) return '';
                return html
                    .replace(/<[^>]*>/g, ' ')
                    .replace(/&nbsp;/g, ' ')
                    .replace(/&amp;/g, '&')
                    .replace(/&lt;/g, '<')
                    .replace(/&gt;/g, '>')
                    .replace(/&quot;/g, '"')
                    .replace(/&#39;/g, "'")
                    .replace(/\s+/g, ' ')
                    .trim();
            };

            // Show toast
            let notificationContent: React.ReactNode = data.content;

            // Check if current page is an auth/public page
            const pathname = window.location.pathname;
            const isAuthPage = ['/login', '/register', '/organization/sign-up', '/forgot-password'].some(p => pathname.startsWith(p));

            // Only trigger full-screen SystemNotificationDialog for broadcast ads or announcements WITHOUT direct target_url, and NEVER on auth/public pages
            if ((data.type === 'ads' || (data.type === 'system' && !data.target_url)) && !isAuthPage) {
                setSystemNotification(data);
                // Dispatch custom event to trigger badge update
                window.dispatchEvent(new CustomEvent('notificationsUpdated'));
                return; // Do not show normal toast for system/ads dialogs
            }

            if (data.from_user && data.from_user.full_name && data.type) {
                const typeKey = `notifications.messages.${data.type}`;
                const translatedString = t(typeKey, '');
                
                if (translatedString && translatedString !== typeKey) {
                    let className = "";
                    if (data.type.includes('join_class') && data.content) {
                        className = data.content.includes('|') ? data.content.split('|')[1] : data.content;
                    }

                    let orgName = "";
                    if (data.type.includes('join_org') && data.content) {
                        if (data.content.includes('|')) {
                            orgName = data.content.split('|')[1] || data.content.split('|')[0];
                        } else if (data.content.includes('yêu cầu gia nhập trường ') && data.content.includes(' của bạn')) {
                            const match = data.content.match(/yêu cầu gia nhập\s+trường\s+(.+?)\s+của bạn/);
                            orgName = match ? match[1] : data.content;
                        } else if (data.content.includes('request to join school ')) {
                            const match = data.content.match(/request to join school\s+(.+?)(?:\.|$)/);
                            orgName = match ? match[1] : data.content;
                        } else {
                            orgName = data.content;
                        }
                    }

                    let lessonName = "";
                    let gameName = "";
                    let exerciseName = "";
                    let studentName = "";

                    if (data.type.startsWith('share_lesson') && data.content) {
                        const parts = data.content.split('|');
                        lessonName = parts[0];
                        studentName = parts[1] || "";
                    } else if (data.type.startsWith('share_game') && data.content) {
                        const parts = data.content.split('|');
                        gameName = parts[0];
                        lessonName = parts[1] || "";
                        studentName = parts[2] || "";
                    } else if ((data.type.startsWith('share_exercise') || data.type === 'submit_exercise' || data.type === 'submit_grading') && data.content) {
                        const parts = data.content.split('|');
                        exerciseName = parts[0];
                        lessonName = parts[1] || "";
                        studentName = parts[2] || "";
                    }

                    notificationContent = (
                        <Trans
                            i18nKey={typeKey}
                            values={{ name: data.from_user.full_name, className, orgName, lessonName, gameName, exerciseName, studentName }}
                            components={[
                                <strong key="0" style={{ fontWeight: 'bold' }} />,
                                <strong key="1" style={{ fontWeight: 'bold' }} />
                            ]}
                        />
                    );
                }
            }

            toast.info(
                <div
                    style={{ cursor: data.target_url ? 'pointer' : 'default' }}
                    onClick={() => {
                        if (data.target_url) {
                            if (data.target_url.startsWith('http://') || data.target_url.startsWith('https://')) {
                                window.open(data.target_url, '_blank');
                            } else {
                                window.location.href = data.target_url;
                            }
                        }
                    }}
                >
                    <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#1e293b' }}>
                        {data.title ? stripHtml(data.title) : t('notifications.new_message', 'Bạn có tin nhắn mới')}
                    </div>
                    <div style={{ fontSize: '0.88rem', color: '#475569', lineHeight: 1.4 }}>
                        {typeof notificationContent === 'string' ? stripHtml(notificationContent) : notificationContent}
                    </div>
                </div>,
                {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "light",
                    icon: () => <NotificationsActiveIcon color="primary" />
                }
            );

            // Request permission if not already granted
            if (Notification.permission === 'default') {
                Notification.requestPermission();
            }

            // Show browser notification
            if (Notification.permission === 'granted') {
                const notif = new Notification(stripHtml(data.title) || 'Trợ giảng AI', {
                    body: stripHtml(data.content) || 'Bạn có thông báo mới',
                    icon: '/favicon.ico'
                });
                
                notif.onclick = () => {
                    window.focus();
                    if (data.target_url) {
                        if (['class_gallery', 'class_post_pending', 'class_post_public', 'class_post_reviewed'].includes(data.type)) {
                            const marketingUrl = process.env.REACT_APP_MARKETING_URL || 'https://trogiang.online';
                            window.open(marketingUrl + data.target_url, '_blank');
                        } else {
                            window.location.href = data.target_url;
                        }
                    }
                };
            }

            // Dispatch custom event to trigger badge update
            window.dispatchEvent(new CustomEvent('notificationsUpdated'));
        });

        newSocket.on('user_status_changed', (data) => {
            window.dispatchEvent(new CustomEvent('userStatusChanged', { detail: data }));
        });

        newSocket.on('disconnect', () => {
            console.log('SocketIO disconnected');
        });

        const handleOpenDialog = (e: any) => {
            if (e.detail) {
                setSystemNotification(e.detail);
            }
        };

        window.addEventListener('openSystemNotification', handleOpenDialog);

        return () => {
            setSharedSocket(null);
            newSocket.disconnect();
            window.removeEventListener('openSystemNotification', handleOpenDialog);
        };
    }, []);

    return (
        <>
            {children}
            <SystemNotificationDialog 
                open={!!systemNotification} 
                data={systemNotification} 
                onClose={() => setSystemNotification(null)} 
            />
        </>
    );
};

export default SocketProvider;
